function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { useObservabilityConfig, useProject } from "@sail/observability";
import { useMemoizedValue } from "@sail/react";
import * as React from "react";
import useDataClient from "../config/useDataClient";
import { buildFetchKey } from "./requestHashing";
import useFetchLog from "./useFetchLog";
import { transformToStatefulPromise } from "../fetchUtils/statefulPromise";
import { _use } from "../hooks/use";
import fullFetch from "../../shared/fetch/fullFetch";
import { REQUEST_SOURCE_HEADER, getRequestSourceHeader, getRequestSourceInformation } from "../../shared/requestSource";
export default function useSuspenseBaseFetch(url, options, format) {
    var _useObservabilityConfig;
    const fetchLog = useFetchLog();
    const { fetchFunction, rawFetchCache } = useDataClient();
    const optionsMemo = useMemoizedValue(options);
    const project = useProject();
    const service = (_useObservabilityConfig = useObservabilityConfig()) === null || _useObservabilityConfig === void 0 ? void 0 : _useObservabilityConfig.service;
    const requestSourceInformation = getRequestSourceInformation({
        service,
        project
    });
    const requestSource = getRequestSourceHeader(requestSourceInformation);
    const fullOptions = React.useMemo(()=>_object_spread_props(_object_spread({}, optionsMemo), {
            headers: _object_spread({
                [REQUEST_SOURCE_HEADER]: requestSource
            }, optionsMemo.headers)
        }), [
        optionsMemo,
        requestSource
    ]);
    const key = React.useMemo(()=>{
        return buildFetchKey(url, fullOptions, format);
    }, [
        url,
        fullOptions,
        format
    ]);
    const subscribeCb = React.useCallback((reactCb)=>rawFetchCache.subscribe(key, reactCb), [
        key,
        rawFetchCache
    ]);
    const getStatefulPromiseCb = React.useCallback(()=>rawFetchCache.getStatefulPromise(key), [
        key,
        rawFetchCache
    ]);
    const cachedPromise = React.useSyncExternalStore(subscribeCb, getStatefulPromiseCb);
    if (cachedPromise) {
        return buildSuspenseResponse(_use(cachedPromise));
    }
    const fetchPromise = fullFetch(url, fullOptions, optionsMemo.prepareFetch ? optionsMemo.prepareFetch(fetchFunction) : fetchFunction, format, fetchLog).then((result)=>{
        if (!result) {
            throw new Error("Fetch didn't return a valid state");
        }
        if (result.error) {
            throw result.error;
        }
        return result;
    });
    const statefulPromise = transformToStatefulPromise(fetchPromise);
    rawFetchCache.addStatefulPromise(key, statefulPromise);
    return buildSuspenseResponse(_use(statefulPromise));
}
// In contrast to non-suspense fetch states, we don't need to check for null values
// because we know that the fetch state is always fulfilled. Otherwise, the use fn
// would throw. Hence we return only a subset of state that is always defined.
function buildSuspenseResponse(fetchState) {
    return {
        called: fetchState.called,
        status: fetchState.status,
        headers: fetchState.headers,
        data: fetchState.data
    };
}

//# sourceMappingURL=useSuspenseBaseFetch.js.map